import React, { Fragment } from "react";

import img13 from "../../../images/service/Layer2/layer2-blockchain-banner1.png";
import img14 from "../../../images/service/Layer2/layer2-blockchain-banner2.png";
import img15 from "../../../images/service/Layer2/layer2-blockchain-banner3.png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Layer-2 Architecture Design",
    content:
      "Design scalable Layer-2 solutions tailored to your application’s performance and cost requirements.",
  },
  {
    icon: sIcon2,
    title: "Rollups & Sidechains Development",
    content:
      "Implement Optimistic Rollups, ZK-Rollups, and sidechains to increase throughput and reduce fees.",
  },
  {
    icon: sIcon3,
    title: "Layer-2 Integration",
    content:
      "Seamlessly integrate Layer-2 networks with Ethereum and existing blockchain applications.",
  },
];

const benefits = [
  {
    title: "Lower Transaction Costs",
    description:
      "Layer-2 solutions significantly reduce gas fees by processing transactions off-chain.",
    icon: sIcon1,
  },
  {
    title: "High Scalability",
    description:
      "Handle thousands of transactions per second without congesting the main blockchain.",
    icon: sIcon2,
  },
  {
    title: "Improved User Experience",
    description:
      "Faster confirmations and lower fees enhance usability for mass adoption.",
    icon: sIcon3,
  },
  {
    title: "Ethereum Security",
    description:
      "Layer-2 solutions inherit the security guarantees of the Ethereum mainnet.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What is Layer-2 blockchain development?",
    a: "Layer-2 blockchain development focuses on building scalability solutions that operate on top of Layer-1 blockchains like Ethereum to improve performance and reduce costs.",
  },
  {
    q: "Why are Layer-2 solutions important?",
    a: "They address scalability and high gas fees while maintaining the security of the underlying blockchain.",
  },
  {
    q: "Which Layer-2 technologies do you support?",
    a: "We work with Optimistic Rollups, ZK-Rollups, sidechains, and other advanced Layer-2 frameworks.",
  },
  {
    q: "Can Layer-2 integrate with existing dApps?",
    a: "Yes. Existing Ethereum dApps can be migrated or integrated with Layer-2 networks seamlessly.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function Layer2BlockchainDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Layer-2 Blockchain Development"
        pagesub="Scalable, cost-efficient & high-performance blockchain solutions"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            Scale Blockchain Apps with{" "}
            <span className="gradient-text">Layer-2 Solutions</span>
          </>
        }
        subtitle={`Overcome scalability and high gas fee challenges by leveraging
advanced Layer-2 blockchain technologies built for mass adoption.`}
        buttonText="Book Consultation"
        buttonLink="/contact"
        image={img13}
        imageAlt="Layer-2 Blockchain Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Layer-2 Blockchain Solutions?"
        image={img14}
        paragraphs={[
          "As blockchain adoption grows, Layer-1 networks face congestion, slow confirmations, and high transaction fees. These limitations restrict scalability and user experience.",
          "Layer-2 solutions process transactions off-chain while settling securely on the main blockchain, delivering high throughput without compromising security.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Our Layer-2 Blockchain Development Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Limitations of Layer-1 Blockchains"
        image={img15}
        paragraphs={[
          "Layer-1 networks prioritize security and decentralization, often at the cost of speed and scalability.",
          "Layer-2 architectures solve this trade-off by moving computation off-chain while preserving trust and finality.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Benefits of Layer-2 Blockchain Development"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Features of Our Layer-2 Solutions"
        subtitle="Designed for scalability, speed, and real-world adoption"
        features={[
          {
            icon: sIcon1,
            title: "Optimistic & ZK Rollups",
            description:
              "Advanced rollup technologies for efficient transaction processing.",
          },
          {
            icon: sIcon2,
            title: "Ethereum Compatibility",
            description:
              "Full compatibility with Ethereum smart contracts and tooling.",
          },
          {
            icon: sIcon3,
            title: "High Throughput",
            description:
              "Support thousands of transactions per second.",
          },
          {
            icon: sIcon4,
            title: "Low Latency",
            description:
              "Faster confirmations for improved user experience.",
          },
          {
            icon: sIcon5,
            title: "Secure Settlement",
            description:
              "Final settlement and security inherited from Layer-1.",
          },
          {
            icon: sIcon6,
            title: "Scalable Infrastructure",
            description:
              "Future-ready architecture for growing user demand.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Expertise in Layer-2 scaling technologies",
          "Performance-optimized blockchain engineering",
          "End-to-end Layer-2 development & integration",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
